<template>
  <Component
    :is="nodeViewWrapper"
    :id="initFileInstance.id"
    :class="[
      'reference-component-wrap',
      { 'reference-component-wrap_custom': type === referenceTypes.FILE }
    ]"
  >
    <span
      :class="[
        'reference-component',
        `reference-component_${type.toLowerCase()}`,
        { 'is-deleted': type === referenceTypes.IDEA && info?.deleted }
      ]"
    >
      <template v-if="!isShowLoading">
        <UserReferenceTooltip
          v-if="type === referenceTypes.USER || type === referenceTypes.PREVIOUS_TASK_OWNER"
          :user="info"
        >
          <div
            class="is-flex"
            @click.prevent
          >
            <span
              class="reference-component__link"
              :class="[{ 'is-disabled': info.enabled === false }]"
            >
              {{ text }}
            </span>
            <span
              v-if="isShowDetails"
              class="reference-component__desc"
            >
              ({{ info.email }})
            </span>
          </div>
        </UserReferenceTooltip>
        <a
          v-if="type === referenceTypes.ITEM"
          class="is-grid"
          :href="href ? href : '#'"
          target="_blank"
          @mousedown.prevent
        >
          <span class="reference-component__link">{{ text }}</span>
          <span
            v-if="info?.itemTypeParsed?.label"
            class="reference-component__desc"
          >
            ({{ info.itemTypeParsed.label }}, {{ info?.statusParsed?.label }})
          </span>
        </a>
        <a
          v-else-if="type === referenceTypes.TASK"
          class="is-flex"
          :href="href ? href : '#'"
          target="_blank"
          @mousedown.prevent
        >
          <span
            class="reference-component__link"
            v-html="text"
          />
          <span
            v-if="taskReferenceDescription"
            class="reference-component__desc"
          >
            ({{ taskReferenceDescription }})</span
          >
        </a>
        <a
          v-else-if="type === referenceTypes.MILESTONE"
          class="is-flex"
          :href="href ? href : '#'"
          target="_blank"
          @mousedown.prevent
        >
          <span class="reference-component__link">{{ text }} </span>
          <span class="reference-component__desc">
            {{ formattedDateStart }} - {{ formattedDateEnd }}
          </span>
        </a>
        <a
          v-else-if="type === referenceTypes.EPIC"
          class="is-flex"
          :href="href ? href : '#'"
          target="_blank"
          @mousedown.prevent
        >
          <span class="reference-component__link">{{ text }}</span>
          <span
            v-if="info?.percentComplete"
            class="reference-component__desc"
          >
            {{ info.percentComplete }}% Done
          </span>
        </a>
        <a
          v-else-if="type === referenceTypes.IDEA"
          class="is-flex"
          :href="href ? href : '#'"
          target="_blank"
          @mousedown.prevent
        >
          <span class="reference-component__link">{{ text }}</span>
          <span
            v-if="ideaReferenceDescription"
            class="reference-component__desc"
          >
            ({{ ideaReferenceDescription }})
          </span>
        </a>
        <a
          v-else-if="type === referenceTypes.PROJECT"
          :href="href ? href : '#'"
          target="_blank"
          @mousedown.prevent
        >
          <span class="reference-component__link">{{ text }}</span>
        </a>
        <a
          v-else-if="type === referenceTypes.SPRINT"
          class="is-flex"
          :href="href ? href : '#'"
          target="_blank"
          @mousedown.prevent
        >
          <span
            v-if="info?.status"
            class="dot dot_sm reference-component__meta"
            :class="[`is-${info.status.toLowerCase()}`]"
          />
          <span class="reference-component__link">{{ text }}</span>
          <span
            v-if="info?.startDate"
            class="reference-component__desc"
          >
            {{ formattedDateStart }} - {{ formattedDateEnd }}
          </span>
          <span
            v-if="info?.team?.name"
            class="reference-component__desc"
          >
            (Team {{ info.team.name }})
          </span>
        </a>
        <a
          v-else-if="type === referenceTypes.TEAM"
          class="is-flex"
          :href="href ? href : '#'"
          target="_blank"
          @mousedown.prevent
        >
          <span class="reference-component__link">{{ text }}</span>
          <span
            v-if="info?.size"
            class="reference-component__desc"
          >
            ({{ info.size }})
          </span>
        </a>
        <FileReferenceComponent
          v-else-if="type === referenceTypes.FILE_LINK"
          :node="node"
          :editor="editor"
          :updateAttributes="updateAttributes"
          :getPos="getPos"
        />
        <div v-else-if="type === referenceTypes.FILE">
          <Attachment
            :node="node"
            :editor="editor"
            :updateAttributes="updateAttributes"
            :getPos="getPos"
          />
        </div>
      </template>
      <span v-else>...Loading...</span>
    </span>
  </Component>
</template>

<script lang="ts">
import { mixins, Options } from "vue-class-component"

import Attachment from "@/components/Attachment/index.vue"
import ReferenceComponentCommon from "@/components/Reference/helpers/ReferenceComponentCommon"

import { FileMetaData } from "@/models"
import { formatDate } from "@/utils/dateUtil"
import { getIdeaStatus } from "@/views/Ideas/constants"

import FileReferenceComponent from "./FileReferenceComponent.vue"
import ParseReference from "./ParseReference"
import UserReferenceTooltip from "./UserReferenceTooltip.vue"

const components = {
  UserReferenceTooltip,
  Attachment,
  FileReferenceComponent
}

@Options({
  name: "InitComponent",
  components
})
export default class extends mixins(ReferenceComponentCommon, ParseReference) {
  get formattedDateStart() {
    return this.info.startDate ? formatDate(this.info.startDate, "dd MMM", { dateOnly: true }) : ""
  }

  get formattedDateEnd() {
    return this.info.endDate ? formatDate(this.info.endDate, "dd MMM", { dateOnly: true }) : ""
  }

  get ideaReferenceDescription() {
    if (this.info?.deleted) return "Deleted"
    return this.info?.status ? getIdeaStatus(this.info.status).label : ""
  }

  get initFileInstance() {
    return FileMetaData.create(this.info)
  }
}
</script>
